import api from './api';

export interface ChequeCartera {
  nroCarga: number;
  nroBanco: number;
  nombreBanco: string;
  nroSucursal: number;
  nombreSucursal: string;
  nroCheque: number;
  nroLibrador: string;
  librador: string;
  importe: number;
  fechaIngreso: string;
  nroMovCajaIngre: number;
  nroDestino: number;
  fechaSalida: string;
  nroMovCajaSalida: number;
  fechaVencimiento: string;
  concepto: string;
  fechaEmision: string;
  conciliado: number;
  asentado: number;
  mcp: string;
  nroCuenta: string;
  comprobanteIngreso?: string;
  comprobanteSalida?: string;
  senial?: number;
}

export interface CuponCartera {
  nroCarga: number;
  codigoMarca: number;
  nombreMarca?: string;
  nroTarjeta: string;
  numeroroAutorizacion: string;
  nroComprobanteTa: number;
  fecha?: string;
  fechaPresentar?: string;
  titularTarjeta?: string;
  telefono?: string;
  montoOperacion: number;
  nroDestino?: number;
  mcp?: string;
  nroIdformulario?: number;
  nroMovCaja?: number;
  nroMovCajaSalida?: number;
  comprobanteIngreso?: string;
  comprobanteSalida?: string;
}

/** Parámetros opcionales de GET /api/Tesoreria/tarjetas-cartera. */
export interface TarjetasCarteraQueryParams {
  searchTerm?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  fechaPresentarDesde?: string;
  fechaPresentarHasta?: string;
  codigoMarca?: number;
  importeDesde?: number;
  importeHasta?: number;
  mcp?: string;
}

/** Parámetros opcionales de GET /api/Tesoreria/cheques-cartera (alineado con ChequesCarteraSearchDto). */
export interface ChequesCarteraQueryParams {
  searchTerm?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  fechaVencimientoDesde?: string;
  fechaVencimientoHasta?: string;
  nroBanco?: number;
  nroSucursal?: number;
  importeDesde?: number;
  importeHasta?: number;
  mcp?: string;
  estado?: string;
  librador?: string;
}

export interface GrupoCajaChica {
  id: number;
  nombre: string;
  codigo: number;
  grupoMcpDestino?: number;
}

export interface Caja {
  nroCaja?: number;
  codigo?: number;
  grupo?: number;
  estado?: string;
  [key: string]: any;
}

export interface GrupoMcpDetalle {
  grupoMcp: number;
  descripcion: string;
  grupoMcpDestino?: number;
}

export interface MedioTransferencia {
  id: number;
  nombre: string; // opcional en UI, mantenemos por compatibilidad
  descripcion?: string; // API: descripcion
  mcp?: string;
  clase?: string;
  // Campos adicionales para derivaciones de payload
  grupo?: number;
  nroSuc?: number;
  tipo?: string;
  cartera?: number;
  nombreBanco?: string;
  cbu?: string;
  nroDeCuenta?: string;
}

export interface MarcaTarjeta {
  /** Código de marca en BD (MarcasTarjetas.CodigoMarca). */
  codigoMarca: number;
  /** Alias de codigoMarca para compatibilidad con selects legacy. */
  id: number;
  nombre: string;
  nombreMarca?: string;
}

export interface Banco {
  id: number;
  nombre: string;
  codigo?: number | string;
  nroBanco?: number | string;
}

/** Payload alineado a ReciboCreateDto (POST /api/Tesoreria/recibos). */
export interface ReciboChequeDetalleCreate {
  nroBanco: number;
  nroSucursal: number;
  nroCheque: string;
  nroLibrador: string;
  librador: string;
  fechaEmision?: string;
  fechaVencimiento?: string;
  ctaBancaria?: string;
  concepto?: string;
}

export interface ReciboTarjetaDetalleCreate {
  codigoMarca: number;
  nroTarjeta?: string;
  nroComprobanteTa?: number;
  fecha?: string;
  fechaPresentar?: string;
  numeroroAutorizacion?: string;
  cuotas?: number;
  telefono?: string;
  titularTarjeta?: string;
}

export interface ReciboMedioCobroCreate {
  mcp: string;
  importe: number;
  detalle?: string;
  cheque?: ReciboChequeDetalleCreate;
  tarjeta?: ReciboTarjetaDetalleCreate;
  cartera?: number;
  /** Fecha de registro del comprobante (solo medios tipo Otros). */
  fecha?: string;
  /** Número de comprobante/certificado (solo medios tipo Otros). */
  nroComprobante?: number;
}

/** Campos opcionales de medios Otros → payload de creación de recibo. */
export function appendMedioOtrosApiFields(
  base: ReciboMedioCobroCreate,
  opts: { fechaRegistro?: string; nroComprobante?: number },
): ReciboMedioCobroCreate {
  const fr = (opts.fechaRegistro || '').trim();
  if (fr) {
    base.fecha = new Date(`${fr}T12:00:00`).toISOString();
  }
  const nro = Number(opts.nroComprobante);
  if (Number.isFinite(nro) && nro > 0) {
    base.nroComprobante = Math.trunc(nro);
  }
  return base;
}

export interface ReciboDocumentoAplicarCreate {
  nroIdformulario: number;
  montoCancelado: number;
}

export interface ReciboCreatePayload {
  nroCliente: number;
  nroSuc: number;
  grupoMCP: number;
  fechaEmision?: string;
  descripcion?: string;
  mediosDeCobro: ReciboMedioCobroCreate[];
  documentosAplicar?: ReciboDocumentoAplicarCreate[];
}

export interface ReciboCreateResponse {
  nroFormulario?: number;
  letra?: string;
  nroSuc?: number;
  nroComprobante?: number;
  importeTotal?: number;
  [key: string]: unknown;
}
export interface GetGruposCajaOptions {
  usuario?: string;
  nroCliente?: number;
}
export interface GetGruposCajaPagedOptions extends GetGruposCajaOptions {
  pageNumber: number;
  pageSize: number;
}

const tesoreriaService = {
  getGruposCajaChica: async (options?: GetGruposCajaOptions): Promise<GrupoCajaChica[]> => {
    const token = localStorage.getItem('token');
    const storedUser =
      options?.usuario ||
      localStorage.getItem('userName') ||
      localStorage.getItem('username') ||
      localStorage.getItem('user') ||
      undefined;
    const response = await api.get<any>('/api/GrupoMcpUsuario', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: {
        pageNumber: 1,
        pageSize: 50, // Límite del backend para GrupoMcpUsuario
        ...(options?.nroCliente ? { nroCliente: options.nroCliente } : {}),
        ...(storedUser ? { usuario: storedUser, Usuario: storedUser } : {}),
      },
    });
    const data = response.data;

    const rawItems: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];

    const mapped: GrupoCajaChica[] = rawItems.map((it: any) => {
      const grupo = it?.grupoMcp ?? it?.grupo ?? it?.id ?? 0;
      return {
        id: grupo,
        nombre: `Grupo MCP ${grupo}`,
        codigo: grupo,
      };
    });

    // Eliminar duplicados por id/código
    const uniqueById = new Map<number, GrupoCajaChica>();
    for (const item of mapped) {
      if (!uniqueById.has(item.id)) {
        uniqueById.set(item.id, item);
      }
    }

    // Enriquecer con descripción real de cada grupo
    const gruposUnicos = Array.from(uniqueById.keys());
    try {
      const detalles = await Promise.all(
        gruposUnicos.map(async (gid) => {
          const detResp = await api.get<GrupoMcpDetalle>(`/api/GrupoMcp/${gid}`, {
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined,
            },
          });
          return detResp.data;
        })
      );

      for (const det of detalles) {
        const current = uniqueById.get(det.grupoMcp);
        if (current) {
          const grupoMcpDestino = det.grupoMcpDestino ?? (det as { GrupoMcpDestino?: number }).GrupoMcpDestino;
          uniqueById.set(det.grupoMcp, {
            ...current,
            nombre: det.descripcion || current.nombre,
            ...(grupoMcpDestino != null && grupoMcpDestino > 0 ? { grupoMcpDestino } : {}),
          });
        }
      }
    } catch (err) {
      // Si falla la descripción, devolvemos los nombres por defecto
      console.warn('No se pudieron cargar descripciones de Grupo MCP', err);
    }

    return Array.from(uniqueById.values());
  },

  getGruposCajaChicaPaged: async (options: GetGruposCajaPagedOptions): Promise<GrupoCajaChica[]> => {
    const token = localStorage.getItem('token');
    const storedUser =
      options?.usuario ||
      localStorage.getItem('userName') ||
      localStorage.getItem('username') ||
      localStorage.getItem('user') ||
      undefined;
    const response = await api.get<any>('/api/GrupoMcpUsuario', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: {
        pageNumber: options.pageNumber,
        pageSize: options.pageSize,
        ...(options?.nroCliente ? { nroCliente: options.nroCliente } : {}),
        ...(storedUser ? { usuario: storedUser, Usuario: storedUser } : {}),
      },
    });
    const data = response.data;

    const rawItems: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];

    const mapped: GrupoCajaChica[] = rawItems.map((it: any) => {
      const grupo = it?.grupoMcp ?? it?.grupo ?? it?.id ?? 0;
      return {
        id: grupo,
        nombre: `Grupo MCP ${grupo}`,
        codigo: grupo,
      };
    });

    // Enriquecer con descripción real de cada grupo (solo de esta página)
    try {
      const detalles = await Promise.all(
        mapped.map(async (g) => {
          const detResp = await api.get<GrupoMcpDetalle>(`/api/GrupoMcp/${g.id}`, {
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined,
            },
          });
          return detResp.data;
        })
      );
      const byId = new Map<number, GrupoCajaChica>(mapped.map(m => [m.id, m]));
      for (const det of detalles) {
        const current = byId.get(det.grupoMcp);
        if (current) {
          const grupoMcpDestino = det.grupoMcpDestino ?? (det as { GrupoMcpDestino?: number }).GrupoMcpDestino;
          byId.set(det.grupoMcp, {
            ...current,
            nombre: det.descripcion || current.nombre,
            ...(grupoMcpDestino != null && grupoMcpDestino > 0 ? { grupoMcpDestino } : {}),
          });
        }
      }
      return Array.from(byId.values());
    } catch (err) {
      console.warn('No se pudieron cargar descripciones de Grupo MCP (paginado)', err);
      return mapped;
    }
  },

  /** Obtiene TODOS los MCPs por tipo desde GET /api/Mcps?Tipo=X, paginando todas las páginas. */
  getMcpsByTipo: async (tipo: string): Promise<MedioTransferencia[]> => {
    const tipoNorm = (tipo ?? '').toString().trim().toUpperCase();
    if (!tipoNorm) return [];
    const token = localStorage.getItem('token');
    const mapItem = (it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: (it?.tipo ?? it?.Tipo ?? '') ? String(it.tipo ?? it.Tipo).trim().toUpperCase() : undefined,
    });
    const allItems: MedioTransferencia[] = [];
    let pageNumber = 1;
    /** Debe coincidir con MaxPageSize del API (McpsController); si no, la paginación corta en la primera página. */
    const pageSize = 50;
    let globalIdx = 0;
    for (let i = 0; i < 200; i++) {
      const response = await api.get<any>('/api/Mcps', {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
        params: { Tipo: tipoNorm, PageNumber: pageNumber, PageSize: pageSize },
      });
      const data = response.data;
      const items: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.value)
        ? data.value
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.items)
        ? data.data.items
        : [];
      if (items.length === 0) break;
      for (const it of items) { allItems.push(mapItem(it, globalIdx++)); }
      if (items.length < pageSize) break;
      pageNumber += 1;
    }
    return allItems;
  },

  /** Obtiene TODAS las chequeras desde GET /api/Mcps (todas las páginas). */
  getMcpsAll: async (): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const mapItem = (it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: (it?.tipo ?? it?.Tipo ?? '') ? String(it.tipo ?? it.Tipo).trim().toUpperCase() : undefined,
    });
    const allItems: MedioTransferencia[] = [];
    let pageNumber = 1;
    const pageSize = 50;
    for (let i = 0; i < 200; i++) {
      const response = await api.get<any>('/api/Mcps', {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
        params: { PageNumber: pageNumber, PageSize: pageSize },
      });
      const data = response.data;
      const items: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.value)
        ? data.value
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.items)
        ? data.data.items
        : [];
      if (items.length === 0) break;
      allItems.push(...items.map(mapItem));
      if (items.length < pageSize) break;
      pageNumber += 1;
    }
    return allItems;
  },

  /** Obtiene TODAS las chequeras de todos los grupos. Intenta GET /api/Mcps; si falla, usa por-grupo. */
  getMcpsPorTipo: async (tipoBuscado: string, grupoActual?: number): Promise<MedioTransferencia[]> => {
    const tipoNorm = (tipoBuscado ?? '').toString().trim().toUpperCase();
    if (!tipoNorm) return [];

    let todos: MedioTransferencia[] = await tesoreriaService.getMcpsAll().catch(() => []);
    if (todos.length === 0) {
      const gruposList = await tesoreriaService.getGruposCajaChica().catch(() => []);
      const grupos = gruposList.length > 0 ? gruposList : (grupoActual != null ? [{ id: grupoActual, nombre: '', codigo: grupoActual }] : []);
      if (grupos.length > 0) {
        const results = await Promise.all(grupos.map((g) => tesoreriaService.getMcpsPorGrupo(g.codigo).catch(() => [] as MedioTransferencia[])));
        todos = results.flat();
      }
    }
    const filtrados = todos.filter((m) => {
      const mTipo = (m.tipo ?? '').toString().trim().toUpperCase();
      const mClase = (m.clase ?? '').toString().trim().toUpperCase();
      const mDesc = (m.descripcion ?? m.nombre ?? '').toString().toUpperCase();
      const mMcp = (m.mcp ?? '').toString().toUpperCase();
      const mFirst = mTipo.charAt(0);
      if (tipoNorm === 'E') {
        if (mFirst === 'P') return false;
        return mTipo === 'E' || mFirst === 'E' || mClase.includes('EFECTIVO') || mDesc.includes('EFECTIVO') || mMcp.includes('EFECTIVO');
      }
      if (tipoNorm === 'P' || tipoNorm === 'C') {
        if (mFirst === 'E') return false;
        return mTipo === tipoNorm || mFirst === tipoNorm || mClase.includes('CHEQUE') || mDesc.includes('CHEQUE') || mMcp.includes('CHEQUE');
      }
      if (tipoNorm === 'T') {
        return mFirst === 'T' || mClase.includes('TARJETA') || mDesc.includes('TARJ') || mMcp.includes('TARJ');
      }
      return mTipo === tipoNorm || mFirst === tipoNorm;
    });
    const byMcp = new Map<string, MedioTransferencia>();
    for (const m of filtrados) {
      const key = (m.mcp ?? '').trim() || `${m.grupo}-${m.id}`;
      if (key && !byMcp.has(key)) byMcp.set(key, m);
    }
    return Array.from(byMcp.values());
  },

  getMcpsPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const mapItem = (it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: (it?.tipo ?? it?.Tipo ?? '') ? String(it.tipo ?? it.Tipo).trim().toUpperCase() : undefined,
    });

    const allItems: MedioTransferencia[] = [];
    let pageNumber = 1;
    const pageSize = 100;

    for (let i = 0; i < 50; i++) {
      const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: { pageNumber, pageSize },
      });
      const data = response.data;
      const items: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.value)
        ? data.value
        : [];

      if (items.length === 0) break;
      allItems.push(...items.map(mapItem));

      const totalPages = Number(data?.totalPages ?? data?.TotalPages ?? 0);
      if (totalPages > 0 && pageNumber >= totalPages) break;
      if (items.length < pageSize) break;
      pageNumber += 1;
    }

    return allItems;
  },

  getMediosTransferenciaPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 }, // Límite del backend para Mcps
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase() === 'TB');

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.nombreBanco ?? it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
      cartera: it?.cartera ?? it?.Cartera ?? undefined,
      nombreBanco: it?.nombreBanco ?? it?.NombreBanco ?? undefined,
      cbu: it?.cbu ?? it?.Cbu ?? undefined,
      nroDeCuenta: it?.nroDeCuenta ?? it?.NroDeCuenta ?? undefined,
    }));
  },

  getMediosEfectivoPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 }, // Límite del backend para Mcps
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase() === 'E');

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
    }));
  },

  getMediosOtrosPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 }, // Límite del backend para Mcps
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase() === 'O');

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
    }));
  },

  getMediosChequesPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 }, // Límite del backend para Mcps
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase() === 'C');

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
      cartera: it?.cartera ?? it?.Cartera ?? undefined,
    }));
  },

  /** Medios de cobro/pago tipo P (cheque propio) del grupo. */
  getMediosChequesPropiosPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase() === 'P');

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
      cartera: it?.cartera ?? it?.Cartera ?? undefined,
      nombreBanco: it?.nombreBanco ?? it?.NombreBanco ?? it?.banco ?? undefined,
    }));
  },

  getMediosTarjetaPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 }, // Límite del backend para Mcps
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => {
      const rawTipo = ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase();
      const rawClase = ((it?.clase ?? it?.Clase ?? '') + '').trim().toUpperCase();
      const rawNombre = ((it?.nombre ?? it?.banco ?? it?.descripcion ?? it?.detalle ?? '') + '')
        .trim()
        .toUpperCase();
      const looksLikeTarjeta =
        rawTipo === 'T' ||
        rawTipo === 'TA' ||
        rawTipo === 'TJ' ||
        rawTipo === 'TC' ||
        rawTipo === 'TD' ||
        rawTipo.startsWith('TARJ') ||
        rawClase.includes('TARJ') ||
        rawNombre.includes('TARJ');
      return looksLikeTarjeta;
    });

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
    }));
  },

  getMediosTarjetaPorGrupoPaged: async (
    grupoMcp: number,
    options: { pageNumber: number; pageSize: number }
  ): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: options.pageNumber, pageSize: options.pageSize },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];
    const filtrados = items.filter((it: any) => {
      const rawTipo = ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase();
      const rawClase = ((it?.clase ?? it?.Clase ?? '') + '').trim().toUpperCase();
      const rawNombre = ((it?.nombre ?? it?.banco ?? it?.descripcion ?? it?.detalle ?? '') + '')
        .trim()
        .toUpperCase();
      const looksLikeTarjeta =
        rawTipo === 'T' ||
        rawTipo === 'TA' ||
        rawTipo === 'TJ' ||
        rawTipo === 'TC' ||
        rawTipo === 'TD' ||
        rawTipo.startsWith('TARJ') ||
        rawClase.includes('TARJ') ||
        rawNombre.includes('TARJ');
      return looksLikeTarjeta;
    });
    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
    }));
  },

  getMarcasTarjetas: async (): Promise<MarcaTarjeta[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>('/api/MarcasTarjetas', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      // MarcasTarjetas no tiene paginación, retorna todos los registros
    });
    const data = response.data;
    const items: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
    const marcas: MarcaTarjeta[] = [];
    for (const it of items) {
      const codigoMarca = Number(it?.codigoMarca ?? it?.CodigoMarca ?? 0);
      if (!Number.isFinite(codigoMarca) || codigoMarca <= 0) continue;
      const nombreMarca = (it?.nombreMarca ?? it?.NombreMarca ?? '').trim();
      const nombre = nombreMarca || (it?.nombre ?? it?.descripcion ?? `Marca ${codigoMarca}`).trim();
      marcas.push({
        codigoMarca,
        id: codigoMarca,
        nombre,
        nombreMarca: nombreMarca || undefined,
      });
    }
    return marcas;
  },

  getBancos: async (searchTerm?: string): Promise<Banco[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>('/api/Banco', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: {
        pageNumber: 1,
        pageSize: 20, // Límite del backend
        searchTerm: searchTerm || undefined,
      },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
    return items.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.codigoBanco ?? it?.nroBanco ?? idx,
      nombre: it?.nombreBanco ?? it?.nombre ?? it?.descripcion ?? `Banco ${idx + 1}`,
      codigo: it?.codigoBanco ?? it?.codigo ?? it?.id,
      nroBanco: it?.nroBanco ?? it?.codigoBanco ?? it?.codigo ?? it?.id,
    }));
  },

  // Obtiene todos los bancos paginando hasta traerlos completos (sin filtro)
  getBancosAll: async (): Promise<Banco[]> => {
    const token = localStorage.getItem('token');
    const pageSize = 20; // Límite del backend
    let pageNumber = 1;
    const acumulados: Banco[] = [];

    // Limitar a 100 páginas como salvaguarda
    for (let i = 0; i < 100; i++) {
      const response = await api.get<any>('/api/Banco', {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: {
          pageNumber,
          pageSize,
        },
      });
      const data = response.data;
      const items: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
      const mapped: Banco[] = items.map((it: any, idx: number) => ({
        id: it?.id ?? it?.codigo ?? it?.codigoBanco ?? it?.nroBanco ?? idx,
        nombre: it?.nombreBanco ?? it?.nombre ?? it?.descripcion ?? `Banco ${idx + 1}`,
        codigo: it?.codigoBanco ?? it?.codigo ?? it?.id,
        nroBanco: it?.nroBanco ?? it?.codigoBanco ?? it?.codigo ?? it?.id,
      }));
      acumulados.push(...mapped);
      if (items.length < pageSize) break;
      pageNumber += 1;
    }

    // Eliminar duplicados por id
    const byId = new Map<number, Banco>();
    for (const b of acumulados) {
      if (!byId.has(b.id)) {
        byId.set(b.id, b);
      }
    }
    return Array.from(byId.values());
  },

  // Paginado simple para infinite scroll
  getBancosPaged: async (options: { searchTerm?: string; pageNumber: number; pageSize: number }): Promise<Banco[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>('/api/Banco', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: {
        pageNumber: options.pageNumber,
        pageSize: options.pageSize,
        searchTerm: options.searchTerm || undefined,
      },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
    return items.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.codigoBanco ?? it?.nroBanco ?? idx,
      nombre: it?.nombreBanco ?? it?.nombre ?? it?.descripcion ?? `Banco ${idx + 1}`,
      codigo: it?.codigoBanco ?? it?.codigo ?? it?.id,
      nroBanco: it?.nroBanco ?? it?.codigoBanco ?? it?.codigo ?? it?.id,
    }));
  },

  /** Bancos de cartera (para depósito/extracción 55/56). Usa GET /api/CarteraBancos. */
  getCarteraBancosPaged: async (options: { searchTerm?: string; tipoDeCuenta?: string; pageNumber: number; pageSize: number }): Promise<Banco[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>('/api/CarteraBancos', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: {
        PageNumber: options.pageNumber,
        PageSize: options.pageSize,
        SearchTerm: options.searchTerm || undefined,
        TipoDeCuenta: options.tipoDeCuenta || undefined,
      },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
    return items.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.codigoBanco ?? it?.nroBanco ?? idx,
      nombre: it?.nombreBanco ?? it?.nombre ?? it?.descripcion ?? `Banco ${idx + 1}`,
      codigo: it?.codigoBanco ?? it?.codigo ?? it?.id,
      nroBanco: it?.nroBanco ?? it?.codigoBanco ?? it?.codigo ?? it?.id,
    }));
  },

  // Obtiene nroSuc asociado al grupo MCP consultando los Mcps del grupo y
  // tomando el primer campo numérico que luzca como sucursal
  getNroSucPorGrupo: async (grupoMcp: number): Promise<number | null> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined },
      params: { pageNumber: 1, pageSize: 50 },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const candidateKeys = [
      'nroSuc', 'NroSuc', 'nrosuc', 'sucursal', 'Sucursal', 'nroSucursal', 'nro_suc', 'suc'
    ];

    for (const it of items) {
      for (const key of candidateKeys) {
        if (key in it) {
          const val = Number(it[key]);
          if (Number.isFinite(val) && val > 0) return val;
        }
      }
    }
    return null;
  },

  // Detalle de un MCP por nombre y tipo (E, C, T, TB, O)
  getMcpDetallePorNombreYTipo: async (
    mcpNombre: string,
    tipo: string
  ): Promise<{ grupo?: number; nroSuc?: number; tipo?: string }> => {
    const token = localStorage.getItem('token');
    const encoded = encodeURIComponent(mcpNombre);
    const response = await api.get<any>(`/api/Mcps/${encoded}/${tipo}`, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined },
    });
    const data = response.data || {};
    return {
      grupo: data?.grupoMcp ?? data?.grupo ?? undefined,
      nroSuc: data?.nroSuc ?? data?.sucursal ?? undefined,
      tipo: data?.tipo ?? tipo,
    };
  },

  // Obtener caja abierta por grupo
  getCajaAbiertaPorGrupo: async (numeroGrupo: number): Promise<Caja | null> => {
    const token = localStorage.getItem('token');
    try {
      const response = await api.get<any>('/api/Caja', {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: {
          Estado: 'ABIERTO',
          Grupo: numeroGrupo,
        },
      });
      const data = response.data;
      // La respuesta puede ser un array o un objeto
      let cajas: any[] = [];
      if (Array.isArray(data)) {
        cajas = data;
      } else if (Array.isArray(data?.items)) {
        cajas = data.items;
      } else if (data && !Array.isArray(data)) {
        // Si es un solo objeto, lo convertimos en array
        cajas = [data];
      }
      
      // Retornar la primera caja abierta encontrada
      if (cajas.length > 0) {
        const caja = cajas[0];
        const codigoCaja = caja.codigoCaja ?? caja.nroCaja ?? caja.codigo ?? caja.id ?? null;
        return {
          nroCaja: codigoCaja,
          codigo: codigoCaja,
          codigoCaja: codigoCaja,
          grupo: caja.grupo ?? numeroGrupo,
          estado: caja.estado ?? 'ABIERTO',
          ...caja,
        };
      }
      return null;
    } catch (error) {
      console.error('Error al obtener caja abierta:', error);
      return null;
    }
  },

  // Obtener cheques de cartera (mismos filtros que informe Cheques en cartera).
  // CodigoCaja es opcional: sin caja se listan cheques del MCP / filtros como en el informe.
  getChequesCartera: async (
    codigoCaja: number | null | undefined,
    pageNumber?: number,
    pageSize?: number,
    filters?: ChequesCarteraQueryParams
  ): Promise<{ cheques: ChequeCartera[]; hasMore: boolean; totalCount?: number }> => {
    const token = localStorage.getItem('token');
    const params: Record<string, string | number> = {
      ...(pageNumber != null ? { pageNumber } : {}),
      ...(pageSize != null ? { pageSize } : {}),
    };
    if (codigoCaja != null && Number(codigoCaja) > 0) {
      params.CodigoCaja = Number(codigoCaja);
    }
    const f = filters;
    if (f?.searchTerm?.trim()) params.searchTerm = f.searchTerm.trim();
    if (f?.fechaDesde?.trim()) params.fechaDesde = f.fechaDesde.trim();
    if (f?.fechaHasta?.trim()) params.fechaHasta = f.fechaHasta.trim();
    if (f?.fechaVencimientoDesde?.trim()) params.fechaVencimientoDesde = f.fechaVencimientoDesde.trim();
    if (f?.fechaVencimientoHasta?.trim()) params.fechaVencimientoHasta = f.fechaVencimientoHasta.trim();
    if (f?.nroBanco != null && Number.isFinite(f.nroBanco)) params.nroBanco = f.nroBanco;
    if (f?.nroSucursal != null && Number.isFinite(f.nroSucursal)) params.nroSucursal = f.nroSucursal;
    if (f?.importeDesde != null && Number.isFinite(f.importeDesde)) params.importeDesde = f.importeDesde;
    if (f?.importeHasta != null && Number.isFinite(f.importeHasta)) params.importeHasta = f.importeHasta;
    if (f?.mcp?.trim()) params.mcp = f.mcp.trim();
    if (f?.estado?.trim()) params.estado = f.estado.trim();
    if (f?.librador?.trim()) params.librador = f.librador.trim();

    const response = await api.get<any>('/api/Tesoreria/cheques-cartera', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params,
    });
    const data = response.data;
    
    // Intentar diferentes estructuras de respuesta
    let cheques: any[] = [];
    if (Array.isArray(data?.chequesCartera)) {
      cheques = data.chequesCartera;
    } else if (Array.isArray(data)) {
      cheques = data;
    } else if (data?.chequesCartera && Array.isArray(data.chequesCartera)) {
      cheques = data.chequesCartera;
    } else if (data?.items && Array.isArray(data.items)) {
      cheques = data.items;
    }
    
    // Determinar si hay más páginas
    // Si no se especificó paginación, asumimos que se devolvieron todos
    let hasMore = false;
    if (pageNumber && pageSize) {
      // Si la respuesta tiene exactamente el tamaño de página, probablemente hay más
      if (cheques.length === pageSize) {
        // Intentar usar indicadores del backend si están disponibles
        if (data?.hasMore !== undefined) {
          hasMore = data.hasMore;
        } else if (data?.totalCount !== undefined) {
          hasMore = data.totalCount > (pageNumber * pageSize);
        } else {
          // Si no hay indicadores, asumimos que hay más si devolvió el tamaño completo de página
          hasMore = true;
        }
      } else {
        // Si devolvió menos que el tamaño de página, no hay más
        hasMore = false;
      }
    }
    
    const chequesMapeados = cheques.map((cheque: any) => ({
      nroCarga: cheque.nroCarga ?? cheque.NroCarga ?? 0,
      nroBanco: cheque.nroBanco ?? cheque.NroBanco ?? 0,
      nombreBanco: cheque.nombreBanco ?? cheque.NombreBanco ?? '',
      nroSucursal: cheque.nroSucursal ?? cheque.NroSucursal ?? 0,
      nombreSucursal: cheque.nombreSucursal ?? cheque.NombreSucursal ?? '',
      nroCheque: cheque.nroCheque ?? cheque.NroCheque ?? 0,
      nroLibrador: cheque.nroLibrador ?? cheque.NroLibrador ?? '',
      librador: cheque.librador ?? cheque.Librador ?? '',
      importe: cheque.importe ?? cheque.Importe ?? 0,
      fechaIngreso: cheque.fechaIngreso ?? cheque.FechaIngreso ?? '',
      nroMovCajaIngre: cheque.nroMovCajaIngre ?? cheque.NroMovCajaIngre ?? 0,
      nroDestino: cheque.nroDestino ?? cheque.NroDestino ?? 0,
      fechaSalida: cheque.fechaSalida ?? cheque.FechaSalida ?? '',
      nroMovCajaSalida: cheque.nroMovCajaSalida ?? cheque.NroMovCajaSalida ?? 0,
      fechaVencimiento: cheque.fechaVencimiento ?? cheque.FechaVencimiento ?? '',
      concepto: cheque.concepto ?? cheque.Concepto ?? '',
      fechaEmision: cheque.fechaEmision ?? cheque.FechaEmision ?? '',
      conciliado: cheque.conciliado ?? cheque.Conciliado ?? 0,
      asentado: cheque.asentado ?? cheque.Asentado ?? 0,
      mcp: cheque.mcp ?? cheque.Mcp ?? cheque.MCP ?? '',
      nroCuenta: cheque.nroCuenta ?? cheque.NroCuenta ?? '',
      comprobanteIngreso: cheque.comprobanteIngreso ?? cheque.ComprobanteIngreso ?? '',
      comprobanteSalida: cheque.comprobanteSalida ?? cheque.ComprobanteSalida ?? '',
      senial: cheque.senial ?? cheque.Senial,
    }));
    
    return {
      cheques: chequesMapeados,
      hasMore,
      totalCount:
        data?.totalCount ?? data?.TotalCount ?? (data?.totalCount === 0 ? 0 : undefined),
    };
  },

  /** Obtiene todos los cheques de cartera paginando (como exportación del informe). */
  getChequesCarteraAll: async (
    codigoCaja: number | null | undefined,
    filters?: ChequesCarteraQueryParams,
    onProgress?: (currentPage: number, totalPages: number) => void
  ): Promise<ChequeCartera[]> => {
    const PAGE_SIZE = 20;
    const MAX_PAGES = 500;
    const allCheques: ChequeCartera[] = [];
    let pageNumber = 1;
    let apiTotalCount = 0;

    while (pageNumber <= MAX_PAGES) {
      const result = await tesoreriaService.getChequesCartera(
        codigoCaja,
        pageNumber,
        PAGE_SIZE,
        filters
      );
      if (result.totalCount != null && Number.isFinite(result.totalCount)) {
        apiTotalCount = result.totalCount;
      }

      if (!result.cheques.length) {
        onProgress?.(Math.max(pageNumber - 1, 1), pageNumber > 1 ? pageNumber - 1 : 1);
        break;
      }

      allCheques.push(...result.cheques);

      const totalPages =
        apiTotalCount > 0 ? Math.max(1, Math.ceil(apiTotalCount / PAGE_SIZE)) : 0;
      onProgress?.(pageNumber, totalPages > 0 ? totalPages : pageNumber);

      if (!result.hasMore) break;
      pageNumber += 1;
    }

    return allCheques;
  },

  getTarjetasCartera: async (
    codigoCaja: number,
    pageNumber?: number,
    pageSize?: number,
    filters?: TarjetasCarteraQueryParams
  ): Promise<{ cupones: CuponCartera[]; hasMore: boolean }> => {
    const token = localStorage.getItem('token');
    const params: Record<string, string | number> = {
      CodigoCaja: codigoCaja,
      ...(pageNumber != null ? { pageNumber } : {}),
      ...(pageSize != null ? { pageSize } : {}),
    };
    const f = filters;
    if (f?.searchTerm?.trim()) params.searchTerm = f.searchTerm.trim();
    if (f?.fechaDesde?.trim()) params.fechaDesde = f.fechaDesde.trim();
    if (f?.fechaHasta?.trim()) params.fechaHasta = f.fechaHasta.trim();
    if (f?.fechaPresentarDesde?.trim()) params.fechaPresentarDesde = f.fechaPresentarDesde.trim();
    if (f?.fechaPresentarHasta?.trim()) params.fechaPresentarHasta = f.fechaPresentarHasta.trim();
    if (f?.codigoMarca != null && Number.isFinite(f.codigoMarca)) params.codigoMarca = f.codigoMarca;
    if (f?.importeDesde != null && Number.isFinite(f.importeDesde)) params.importeDesde = f.importeDesde;
    if (f?.importeHasta != null && Number.isFinite(f.importeHasta)) params.importeHasta = f.importeHasta;
    if (f?.mcp?.trim()) params.mcp = f.mcp.trim();

    const response = await api.get<any>('/api/Tesoreria/tarjetas-cartera', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params,
    });
    const data = response.data;

    let cupones: any[] = [];
    if (Array.isArray(data?.tarjetasCartera)) {
      cupones = data.tarjetasCartera;
    } else if (Array.isArray(data)) {
      cupones = data;
    } else if (data?.items && Array.isArray(data.items)) {
      cupones = data.items;
    }

    let hasMore = false;
    if (pageNumber && pageSize) {
      if (cupones.length === pageSize) {
        if (data?.hasMore !== undefined) {
          hasMore = data.hasMore;
        } else if (data?.totalCount !== undefined) {
          hasMore = data.totalCount > pageNumber * pageSize;
        } else {
          hasMore = true;
        }
      }
    }

    const cuponesMapeados: CuponCartera[] = cupones.map((c: any) => ({
      nroCarga: c.nroCarga ?? 0,
      codigoMarca: c.codigoMarca ?? 0,
      nombreMarca: c.nombreMarca ?? '',
      nroTarjeta: c.nroTarjeta ?? '',
      numeroroAutorizacion: c.numeroroAutorizacion ?? '',
      nroComprobanteTa: c.nroComprobanteTa ?? 0,
      fecha: c.fecha ?? '',
      fechaPresentar: c.fechaPresentar ?? '',
      titularTarjeta: c.titularTarjeta ?? '',
      telefono: c.telefono ?? '',
      montoOperacion: c.montoOperacion ?? 0,
      nroDestino: c.nroDestino ?? 0,
      mcp: c.mcp ?? '',
      nroIdformulario: c.nroIdformulario ?? 0,
      nroMovCaja: c.nroMovCaja ?? 0,
      nroMovCajaSalida: c.nroMovCajaSalida ?? 0,
      comprobanteIngreso: c.comprobanteIngreso ?? '',
      comprobanteSalida: c.comprobanteSalida ?? '',
    }));

    return { cupones: cuponesMapeados, hasMore };
  },

  getTransferenciasCaja: async (): Promise<any[]> => {
    const token = localStorage.getItem('token');
    const extractItems = (data: any): any[] =>
      Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.value)
        ? data.value
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.items)
        ? data.data.items
        : [];
    try {
      const response = await api.get<any>('/api/TesoreriaCaja/transferencias-caja', {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      return extractItems(response.data);
    } catch {
      return [];
    }
  },

  getTransferenciasCajaPaged: async (params: {
    pageNumber: number;
    pageSize: number;
    searchTerm?: string | null;
    fechaDesde?: string | null;
    fechaHasta?: string | null;
    nroSuc?: number;
  }): Promise<{ items: any[]; totalCount: number }> => {
    const token = localStorage.getItem('token');
    const extractItems = (data: any): any[] =>
      Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.value)
        ? data.value
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.items)
        ? data.data.items
        : [];
    const getTotalCount = (data: any): number =>
      data?.totalCount ?? data?.total ?? data?.count ?? 0;
    try {
      const apiParams: Record<string, any> = {
        PageNumber: params.pageNumber,
        PageSize: params.pageSize,
      };
      if (params.searchTerm) apiParams.SearchTerm = params.searchTerm;
      if (params.fechaDesde) apiParams.FechaDesde = params.fechaDesde;
      if (params.fechaHasta) apiParams.FechaHasta = params.fechaHasta;
      if (params.nroSuc != null && params.nroSuc > 0) apiParams.NroSuc = params.nroSuc;
      const response = await api.get<any>('/api/TesoreriaCaja/transferencias-caja', {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
        params: apiParams,
      });
      const items = extractItems(response.data);
      const totalCount = getTotalCount(response.data);
      return { items, totalCount: totalCount > 0 ? totalCount : items.length };
    } catch {
      return { items: [], totalCount: 0 };
    }
  },

  postTransferenciaCaja: async (payload: any): Promise<any> => {
    const token = localStorage.getItem('token');
    const response = await api.post<any>('/api/TesoreriaCaja/transferencias-caja', payload, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined },
    });
    return response.data;
  },

  puedeAnularRecibo: async (id: number): Promise<{ puedeAnular: boolean; motivos: string[] }> => {
    const response = await api.get<{ puedeAnular: boolean; motivos: string[] }>(
      `/api/Tesoreria/recibos/${id}/puede-anular`
    );
    return response.data;
  },

  anularRecibo: async (id: number, motivo?: string): Promise<void> => {
    await api.post(`/api/Tesoreria/recibos/${id}/anular`, { motivo: motivo ?? null });
  },

  anularOrdenPago: async (id: number): Promise<void> => {
    await api.post(`/api/Tesoreria/ordendepago/${id}/anular`);
  },

  createRecibo: async (payload: ReciboCreatePayload): Promise<ReciboCreateResponse> => {
    const response = await api.post<ReciboCreateResponse>('/api/Tesoreria/recibos', payload);
    return response.data;
  },
};

export default tesoreriaService;

